import { PostgrestError } from '@supabase/supabase-js';

// ===== DATABASE ERROR HANDLING =====
export interface DatabaseError {
  code: string;
  message: string;
  details?: string;
  hint?: string;
  isRetryable: boolean;
  userMessage: string;
}

export class DatabaseErrorHandler {
  /**
   * Parse Supabase/PostgreSQL errors into user-friendly messages
   */
  static parseError(error: PostgrestError | Error): DatabaseError {
    // Handle PostgrestError (Supabase errors)
    if ('code' in error && 'details' in error) {
      const pgError = error as PostgrestError;
      
      switch (pgError.code) {
        case '23505': // unique_violation
          return {
            code: pgError.code,
            message: pgError.message,
            details: pgError.details,
            hint: pgError.hint,
            isRetryable: false,
            userMessage: 'This record already exists. Please check your data and try again.'
          };
          
        case '23503': // foreign_key_violation
          return {
            code: pgError.code,
            message: pgError.message,
            details: pgError.details,
            hint: pgError.hint,
            isRetryable: false,
            userMessage: 'Referenced record not found. Please ensure all required data exists.'
          };
          
        case '23502': // not_null_violation
          return {
            code: pgError.code,
            message: pgError.message,
            details: pgError.details,
            hint: pgError.hint,
            isRetryable: false,
            userMessage: 'Required field is missing. Please fill in all required information.'
          };
          
        case '42501': // insufficient_privilege
          return {
            code: pgError.code,
            message: pgError.message,
            details: pgError.details,
            hint: pgError.hint,
            isRetryable: false,
            userMessage: 'You do not have permission to perform this action.'
          };
          
        case 'PGRST116': // not found
          return {
            code: pgError.code,
            message: pgError.message,
            details: pgError.details,
            hint: pgError.hint,
            isRetryable: false,
            userMessage: 'Record not found.'
          };
          
        case 'PGRST301': // JWT expired
          return {
            code: pgError.code,
            message: pgError.message,
            details: pgError.details,
            hint: pgError.hint,
            isRetryable: true,
            userMessage: 'Your session has expired. Please sign in again.'
          };
          
        default:
          return {
            code: pgError.code || 'UNKNOWN',
            message: pgError.message,
            details: pgError.details,
            hint: pgError.hint,
            isRetryable: true,
            userMessage: 'An unexpected error occurred. Please try again.'
          };
      }
    }
    
    // Handle generic JavaScript errors
    return {
      code: 'JS_ERROR',
      message: error.message,
      isRetryable: true,
      userMessage: 'An unexpected error occurred. Please try again.'
    };
  }

  /**
   * Log error with context
   */
  static logError(error: DatabaseError, context: {
    operation: string;
    table?: string;
    userId?: string;
    data?: any;
  }) {
    console.error(`🔥 Database Error [${context.operation}]`, {
      error,
      context,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Check if error should trigger a retry
   */
  static shouldRetry(error: DatabaseError, attemptCount: number, maxAttempts: number = 3): boolean {
    return error.isRetryable && attemptCount < maxAttempts;
  }
}

// ===== VALIDATION UTILITIES =====
export class DatabaseValidator {
  /**
   * Validate email format
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate UUID format
   */
  static isValidUUID(uuid: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  /**
   * Validate user data
   */
  static validateUserData(data: {
    firebase_uid: string;
    email: string;
    display_name?: string;
    phone_number?: string;
  }): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.firebase_uid || data.firebase_uid.length < 10) {
      errors.push('Invalid Firebase UID');
    }

    if (!data.email || !this.isValidEmail(data.email)) {
      errors.push('Invalid email address');
    }

    if (data.display_name && data.display_name.length > 100) {
      errors.push('Display name too long (max 100 characters)');
    }

    if (data.phone_number && !/^\+?[\d\s\-()]+$/.test(data.phone_number)) {
      errors.push('Invalid phone number format');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate purchase data
   */
  static validatePurchaseData(data: {
    firebase_uid: string;
    product_id: string;
    product_name: string;
    unit_price: number;
    total_amount: number;
  }): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.firebase_uid) {
      errors.push('Firebase UID is required');
    }

    if (!data.product_id || data.product_id.length < 1) {
      errors.push('Product ID is required');
    }

    if (!data.product_name || data.product_name.length < 1) {
      errors.push('Product name is required');
    }

    if (typeof data.unit_price !== 'number' || data.unit_price < 0) {
      errors.push('Unit price must be a positive number');
    }

    if (typeof data.total_amount !== 'number' || data.total_amount < 0) {
      errors.push('Total amount must be a positive number');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Sanitize text input
   */
  static sanitizeText(text: string, maxLength: number = 1000): string {
    return text
      .trim()
      .substring(0, maxLength)
      .replace(/[<>"']/g, ''); // Remove potentially dangerous characters
  }

  /**
   * Validate analytics event data
   */
  static validateAnalyticsData(data: {
    firebase_uid: string;
    event_type: string;
    credits_used?: number;
    cost_incurred?: number;
  }): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.firebase_uid) {
      errors.push('Firebase UID is required');
    }

    if (!data.event_type || data.event_type.length < 1) {
      errors.push('Event type is required');
    }

    if (data.credits_used !== undefined && (typeof data.credits_used !== 'number' || data.credits_used < 0)) {
      errors.push('Credits used must be a positive number');
    }

    if (data.cost_incurred !== undefined && (typeof data.cost_incurred !== 'number' || data.cost_incurred < 0)) {
      errors.push('Cost incurred must be a positive number');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// ===== RETRY UTILITIES =====
export class DatabaseRetry {
  /**
   * Retry database operation with exponential backoff
   */
  static async withRetry<T>(
    operation: () => Promise<{ data: T | null; error: any }>,
    options: {
      maxAttempts?: number;
      baseDelay?: number;
      maxDelay?: number;
      operationName?: string;
    } = {}
  ): Promise<{ data: T | null; error: any }> {
    const {
      maxAttempts = 3,
      baseDelay = 1000,
      maxDelay = 10000,
      operationName = 'Database Operation'
    } = options;

    let lastError: any = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const result = await operation();
        
        if (result.error) {
          const dbError = DatabaseErrorHandler.parseError(result.error);
          
          if (!DatabaseErrorHandler.shouldRetry(dbError, attempt, maxAttempts)) {
            DatabaseErrorHandler.logError(dbError, {
              operation: operationName,
              userId: 'unknown'
            });
            return result;
          }
          
          lastError = result.error;
          
          if (attempt < maxAttempts) {
            const delay = Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);
            console.warn(`⚠️ ${operationName} failed (attempt ${attempt}/${maxAttempts}), retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        } else {
          // Success
          if (attempt > 1) {
            console.log(`✅ ${operationName} succeeded on attempt ${attempt}`);
          }
          return result;
        }
      } catch (error) {
        lastError = error;
        
        if (attempt < maxAttempts) {
          const delay = Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);
          console.warn(`⚠️ ${operationName} threw error (attempt ${attempt}/${maxAttempts}), retrying in ${delay}ms...`, error);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All attempts failed
    const dbError = DatabaseErrorHandler.parseError(lastError);
    DatabaseErrorHandler.logError(dbError, {
      operation: operationName,
      userId: 'unknown'
    });

    return { data: null, error: lastError };
  }
}

// ===== QUERY OPTIMIZATION UTILITIES =====
export class QueryOptimizer {
  /**
   * Build optimized select query with only needed fields
   */
  static buildSelectQuery(fields: string[] = ['*']): string {
    return fields.join(',');
  }

  /**
   * Build pagination parameters
   */
  static buildPagination(page: number = 1, limit: number = 20): { from: number; to: number } {
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    return { from, to };
  }

  /**
   * Build date range filter
   */
  static buildDateRange(startDate?: string, endDate?: string): {
    gte?: string;
    lte?: string;
  } {
    const filters: { gte?: string; lte?: string } = {};
    
    if (startDate) {
      filters.gte = startDate;
    }
    
    if (endDate) {
      filters.lte = endDate;
    }
    
    return filters;
  }

  /**
   * Optimize JSONB queries
   */
  static buildJsonbFilter(field: string, path: string, value: any): string {
    return `${field}->>'${path}'`;
  }
}

// ===== PERFORMANCE MONITORING =====
export class DatabaseMonitor {
  private static queryTimes: Map<string, number[]> = new Map();

  /**
   * Start timing a database operation
   */
  static startTimer(operationName: string): () => void {
    const startTime = Date.now();
    
    return () => {
      const duration = Date.now() - startTime;
      this.recordQueryTime(operationName, duration);
      
      if (duration > 5000) { // Log slow queries (>5s)
        console.warn(`🐌 Slow query detected: ${operationName} took ${duration}ms`);
      }
    };
  }

  /**
   * Record query execution time
   */
  private static recordQueryTime(operationName: string, duration: number): void {
    if (!this.queryTimes.has(operationName)) {
      this.queryTimes.set(operationName, []);
    }
    
    const times = this.queryTimes.get(operationName)!;
    times.push(duration);
    
    // Keep only last 100 measurements
    if (times.length > 100) {
      times.shift();
    }
  }

  /**
   * Get performance statistics
   */
  static getStats(operationName?: string): Record<string, any> {
    if (operationName) {
      const times = this.queryTimes.get(operationName) || [];
      if (times.length === 0) return {};
      
      const sorted = [...times].sort((a, b) => a - b);
      return {
        operation: operationName,
        count: times.length,
        avg: Math.round(times.reduce((a, b) => a + b, 0) / times.length),
        min: sorted[0],
        max: sorted[sorted.length - 1],
        p50: sorted[Math.floor(sorted.length * 0.5)],
        p95: sorted[Math.floor(sorted.length * 0.95)],
        p99: sorted[Math.floor(sorted.length * 0.99)]
      };
    }
    
    // Return stats for all operations
    const allStats: Record<string, any> = {};
    this.queryTimes.forEach((_, name) => {
      allStats[name] = this.getStats(name);
    });
    return allStats;
  }
}

// All utilities are already exported as classes above
